import { Pollutant } from "@/types/pollutant/pollutant.types";
import { Column } from "@/components/ui/data-table/types";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatNumberDynamic } from "@/utils/numberFormatter";

export const usePollutantColumns = (
  t: (key: string) => string,
  onEdit: (pollutant: Pollutant) => void,
  onDelete: (pollutant: Pollutant) => void
): Column<Pollutant>[] => {
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  
  const baseColumns: Column<Pollutant>[] = [
    {
      header: t("pollutants.table.name"),
      accessorKey: "name",
      sortable: true,
      className: "min-w-[180px]",
    },
    {
      header: t("pollutants.table.polu_vma"),
      accessorKey: "polu_vma",
      sortable: true,
      className: "min-w-[120px]",
      cell: (_: string, row: Pollutant) => (
        <div>{row.polu_vma !== undefined ? formatNumberDynamic(row.polu_vma) : "-"}</div>
      ),
    },
  ];

  const actionsColumn: Column<Pollutant> = {
    header: t("pollutants.table.actions"),
    accessorKey: "id",
    sortable: false,
    className: "w-[100px]",
    cell: (_: string, row: Pollutant) => (
      <div className="flex items-center gap-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(row)}
                className="h-8 w-8 hover:bg-gray-100"
              >
                <Pencil className="h-4 w-4 text-gray-500" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{t("pollutants.actions.edit")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(row)}
                className="h-8 w-8 hover:bg-gray-100 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4 text-gray-500" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{t("pollutants.actions.delete")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
  };

  return isAssimagraUser ? baseColumns : [...baseColumns, actionsColumn];
};
